import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { getAllUsers, deactivateUser, activateUser, getReferralLeaderboard } from "../services/userService";
import { toast } from "react-toastify";
import {
  FiUsers,
  FiUserX,
  FiUserCheck,
  FiSearch,
  FiAward,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiMail,
  FiShield,
  FiAlertTriangle,
  FiX,
  FiCalendar,
  FiEye,
} from "react-icons/fi";
import { MdLeaderboard } from "react-icons/md";


/* ── Format date helper ── */
const formatDate = (dateString) => {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const statusBadge = (status) => {
  const isActive = status === "ACTIVE";
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
        isActive ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isActive ? "bg-emerald-500" : "bg-red-500"}`}
      />
      {isActive ? "Active" : "Inactive"}
    </span>
  );
};

const roleBadge = (role) => (
  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-600">
    <FiShield className="text-[10px]" />
    {role}
  </span>
);

const Avatar = ({ name, pictureUrl }) => {
  if (pictureUrl) {
    return (
      <img
        src={pictureUrl}
        alt={name}
        className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow-sm"
        onError={(e) => {
          e.target.style.display = "none";
          e.target.nextSibling.style.display = "flex";
        }}
      />
    );
  }
  const initials = name
    ? name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";
  return (
    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center text-white text-xs font-bold ring-2 ring-white shadow-sm">
      {initials}
    </div>
  );
};

const rankStyle = {
  1: { bg: "bg-amber-100", text: "text-amber-600", label: "🥇" },
  2: { bg: "bg-gray-100", text: "text-gray-500", label: "🥈" },
  3: { bg: "bg-orange-100", text: "text-orange-500", label: "🥉" },
};

/* ── Status Change Confirmation Modal ── */
const StatusModal = ({ user, onConfirm, onCancel, submitting }) => {
  if (!user) return null;
  const isActive = user.status === "ACTIVE";
  const initials = user.name
    ? user.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={!submitting ? onCancel : undefined}
      />
      {/* modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* top colour bar */}
        <div className={`h-1.5 w-full ${isActive ? "bg-red-500" : "bg-emerald-500"}`} />

        <div className="p-6">
          {/* close */}
          <button
            onClick={onCancel}
            disabled={submitting}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition disabled:opacity-40"
          >
            <FiX />
          </button>

          {/* icon */}
          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${
              isActive ? "bg-red-50" : "bg-emerald-50"
            }`}
          >
            {isActive ? (
              <FiUserX className="text-2xl text-red-500" />
            ) : (
              <FiUserCheck className="text-2xl text-emerald-500" />
            )}
          </div>

          <h2 className="text-lg font-bold text-gray-800">
            {isActive ? "Deactivate User" : "Activate User"}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {isActive
              ? "This will prevent the user from accessing the app."
              : "This will restore the user's access to the app."}
          </p>

          {/* user card */}
          <div className="mt-5 flex items-center gap-3 p-3.5 rounded-xl bg-gray-50 border border-gray-100">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-800 truncate">{user.fullName || user.name}</div>
              <div className="text-xs text-gray-400 truncate">{user.email}</div>
            </div>
            <span
              className={`ml-auto shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                isActive ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isActive ? "bg-emerald-500" : "bg-red-500"}`} />
              {isActive ? "Active" : "Inactive"}
            </span>
          </div>

          {/* warning note */}
          <div className={`mt-4 flex items-start gap-2.5 p-3 rounded-lg text-xs ${
            isActive ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"
          }`}>
            <FiAlertTriangle className="shrink-0 mt-0.5" />
            <span>
              {isActive
                ? `Are you sure you want to deactivate "${user.name}"? They will lose access immediately.`
                : `Are you sure you want to activate "${user.name}"? They will regain full access.`}
            </span>
          </div>

          {/* actions */}
          <div className="mt-6 flex items-center gap-3 justify-end">
            <button
              onClick={onCancel}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={submitting}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white transition disabled:opacity-60 disabled:cursor-not-allowed ${
                isActive
                  ? "bg-red-500 hover:bg-red-600 shadow-md shadow-red-200"
                  : "bg-emerald-500 hover:bg-emerald-600 shadow-md shadow-emerald-200"
              }`}
            >
              {submitting ? (
                <FiRefreshCw className="animate-spin" />
              ) : isActive ? (
                <FiUserX />
              ) : (
                <FiUserCheck />
              )}
              {submitting
                ? isActive ? "Deactivating…" : "Activating…"
                : isActive ? "Yes, Deactivate" : "Yes, Activate"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [createdTimeFrom, setCreatedTimeFrom] = useState("");
  const [createdTimeTo, setCreatedTimeTo] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [sortOrder, setSortOrder] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("ALL"); // 'ALL' | 'ACTIVE' | 'INACTIVE'

  /* ── status modal state ── */
  const [modalUser, setModalUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [activeTab, setActiveTab] = useState("users"); // 'users' | 'leaderboard'
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardPage, setLeaderboardPage] = useState(1);
  const [leaderboardPageSize, setLeaderboardPageSize] = useState(50);
  const [leaderboardTotalElements, setLeaderboardTotalElements] = useState(0);

  /* ── fetch users ── */
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllUsers(0, 100000);
      if (res.success && res.data) {
        setUsers(res.data.content || []);
      } else {
        toast.error("Failed to load users");
      }
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── fetch leaderboard ── */
  const fetchLeaderboard = useCallback(async () => {
    setLeaderboardLoading(true);
    try {
      const res = await getReferralLeaderboard(leaderboardPage - 1, leaderboardPageSize);
      if (res.success && res.data) {
        setLeaderboard(res.data.content || []);
        setLeaderboardTotalElements(res.data.totalElements || 0);
      } else {
        toast.error("Failed to load leaderboard");
      }
    } catch {
      toast.error("Failed to load leaderboard");
    } finally {
      setLeaderboardLoading(false);
    }
  }, [leaderboardPage, leaderboardPageSize]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (activeTab === "leaderboard") {
      fetchLeaderboard();
    }
  }, [activeTab, fetchLeaderboard]);

  /* ── status change handler ── */
  const handleStatusChange = async () => {
    if (!modalUser) return;
    const isActive = modalUser.status === "ACTIVE";
    setSubmitting(true);
    try {
      const res = isActive
        ? await deactivateUser(modalUser.id)
        : await activateUser(modalUser.id);
      if (res.success) {
        const newStatus = isActive ? "INACTIVE" : "ACTIVE";
        toast.success(
          `${modalUser.name} has been ${isActive ? "deactivated" : "activated"}`
        );
        setUsers((prev) =>
          prev.map((u) =>
            u.id === modalUser.id ? { ...u, status: newStatus } : u
          )
        );
        setModalUser(null);
      }
    } catch {
      toast.error(`Failed to ${isActive ? "deactivate" : "activate"} user`);
    } finally {
      setSubmitting(false);
    }
  };

  /* Stats derived directly from users state */
  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.status === "ACTIVE").length;
  const inactiveUsers = totalUsers - activeUsers;

  /* ── derived ── */
  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    const matchesSearch =
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q);

    if (!matchesSearch) return false;

    /* User Status filter */
    if (statusFilter !== "ALL" && u.status !== statusFilter) return false;

    /* Date filter */
    if (createdTimeFrom || createdTimeTo) {
      const userDate = new Date(u.createdTime);
      if (createdTimeFrom && userDate < new Date(createdTimeFrom)) return false;
      if (createdTimeTo) {
        const endDate = new Date(createdTimeTo);
        endDate.setHours(23, 59, 59, 999);
        if (userDate > endDate) return false;
      }
    }

    return true;
  });

  // Sort by createdTime: desc (newest first) or asc (oldest first)
  const sorted = [...filtered].sort((a, b) => {
    const dateA = new Date(a.createdTime || 0);
    const dateB = new Date(b.createdTime || 0);
    return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
  });

  const totalElements = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  /* ── reset to page 1 on search change ── */
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  /* ── handle date filter change ── */
  const handleDateFilterChange = () => {
    setPage(1);
  };

  /* ── reset filters ── */
  const resetFilters = () => {
    setSearch("");
    setStatusFilter("ALL");
    setCreatedTimeFrom("");
    setCreatedTimeTo("");
    setSortOrder("desc");
    setPage(1);
  };

  return (
    <div>
      {/* Status Change Modal */}
      <StatusModal
        user={modalUser}
        onConfirm={handleStatusChange}
        onCancel={() => !submitting && setModalUser(null)}
        submitting={submitting}
      />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Users Management</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage registered users and view referral performance
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          {
            key: "ALL",
            label: "Total Users",
            value: totalUsers,
            icon: <FiUsers />,
            color: "text-indigo-500",
            bg: "bg-indigo-50",
            ring: "ring-2 ring-indigo-400 bg-indigo-50/20",
          },
          {
            key: "ACTIVE",
            label: "Active Users",
            value: activeUsers,
            icon: <FiUserCheck />,
            color: "text-emerald-500",
            bg: "bg-emerald-50",
            ring: "ring-2 ring-emerald-500 bg-emerald-50/20",
          },
          {
            key: "INACTIVE",
            label: "Inactive Users",
            value: inactiveUsers,
            icon: <FiUserX />,
            color: "text-red-500",
            bg: "bg-red-50",
            ring: "ring-2 ring-red-400 bg-red-50/20",
          },
        ].map((s) => (
          <button
            key={s.label}
            onClick={() => {
              setStatusFilter(s.key);
              setPage(1);
            }}
            className={`flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm hover:-translate-y-0.5 hover:shadow-md transition text-left w-full cursor-pointer border border-gray-100 ${
              statusFilter === s.key ? s.ring : ""
            }`}
          >
            <div
              className={`w-12 h-12 rounded-xl ${s.bg} ${s.color} flex items-center justify-center text-xl shrink-0`}
            >
              {s.icon}
            </div>
            <div>
              <div className="text-xl font-bold text-gray-800">{loading ? "—" : s.value}</div>
              <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                <span>{s.label}</span>
                {statusFilter === s.key && (
                  <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.2 rounded">Filtered</span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 gap-4 flex-wrap">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab("users")}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition ${
                activeTab === "users"
                  ? "bg-white shadow text-gray-800"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <FiUsers className="text-base" /> All Users
            </button>
            <button
              onClick={() => setActiveTab("leaderboard")}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition ${
                activeTab === "leaderboard"
                  ? "bg-white shadow text-gray-800"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <MdLeaderboard className="text-base" /> Referral Leaderboard
            </button>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {activeTab === "users" ? (
              <div className="relative flex-1 sm:flex-none">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={search}
                  onChange={handleSearch}
                  className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-300 w-full sm:w-56"
                />
              </div>
            ) : (
              <button
                onClick={fetchLeaderboard}
                disabled={leaderboardLoading}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                <FiRefreshCw className={`text-sm ${leaderboardLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            )}
          </div>
        </div>

        {/* ── Status, Date Filter & Sort ── */}
        {activeTab === "users" && (
          <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/50">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2.5">
                {/* Active / Inactive Status Pills */}
                <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1 shadow-xs">
                  <button
                    onClick={() => {
                      setStatusFilter("ALL");
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                      statusFilter === "ALL"
                        ? "bg-[#1e1e2d] text-white shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    }`}
                  >
                    All ({totalUsers})
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter("ACTIVE");
                      setPage(1);
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                      statusFilter === "ACTIVE"
                        ? "bg-emerald-600 text-white shadow-sm"
                        : "text-emerald-700 hover:bg-emerald-50"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${statusFilter === "ACTIVE" ? "bg-white" : "bg-emerald-500"}`} />
                    Active ({activeUsers})
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter("INACTIVE");
                      setPage(1);
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                      statusFilter === "INACTIVE"
                        ? "bg-red-600 text-white shadow-sm"
                        : "text-red-600 hover:bg-red-50"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${statusFilter === "INACTIVE" ? "bg-white" : "bg-red-500"}`} />
                    Inactive ({inactiveUsers})
                  </button>
                </div>

                <button
                  onClick={() => setShowDateFilter(!showDateFilter)}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition"
                >
                  <FiCalendar className="text-xs" />
                  Date Filter
                  {(createdTimeFrom || createdTimeTo) && (
                    <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.2 rounded-full">
                      Active
                    </span>
                  )}
                </button>
                <select
                  value={sortOrder}
                  onChange={(e) => {
                    setSortOrder(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-300"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>

              {(createdTimeFrom || createdTimeTo || statusFilter !== "ALL" || search) && (
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                >
                  <FiX className="text-xs" /> Clear filters
                </button>
              )}
            </div>

            {showDateFilter && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-2">From Date</label>
                    <input
                      type="date"
                      value={createdTimeFrom}
                      onChange={(e) => {
                        setCreatedTimeFrom(e.target.value);
                        handleDateFilterChange();
                      }}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-2">To Date</label>
                    <input
                      type="date"
                      value={createdTimeTo}
                      onChange={(e) => {
                        setCreatedTimeTo(e.target.value);
                        handleDateFilterChange();
                      }}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Users Table ── */}
        {activeTab === "users" && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">#</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">User</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      <span className="flex items-center gap-1"><FiMail className="text-xs" /> Email</span>
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Role</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      <span className="flex items-center gap-1"><FiCalendar className="text-xs" /> Created</span>
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      <span className="flex items-center gap-1"><FiCalendar className="text-xs" /> Updated</span>
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 8 }).map((__, j) => (
                          <td key={j} className="px-5 py-3.5">
                            <div className="h-4 bg-gray-100 rounded animate-pulse" style={{ width: j === 1 ? "160px" : "80px" }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : paginated.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-5 py-16 text-center text-gray-400 text-sm">
                        <FiUsers className="text-4xl mx-auto mb-3 text-gray-200" />
                        {search ? "No users match your search." : "No users found."}
                      </td>
                    </tr>
                  ) : (
                    paginated.map((user, idx) => (
                      <tr key={user.id} className="hover:bg-gray-50/70 transition">
                        <td className="px-5 py-3.5 text-sm text-gray-400">
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className="px-5 py-3.5">
                          <Link
                            to={`/dashboard/users/${user.id}`}
                            className="flex items-center gap-3 group"
                          >
                            <Avatar name={user.name} pictureUrl={user.pictureUrl} />
                            <div>
                              <div className="text-sm font-semibold text-gray-800 leading-tight group-hover:text-red-600 group-hover:underline transition underline-offset-2">
                                {user.fullName || user.name}
                              </div>
                              {user.fullName && user.name !== user.fullName && (
                                <div className="text-xs text-gray-400">@{user.name}</div>
                              )}
                            </div>
                          </Link>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-600">{user.email}</td>
                        <td className="px-5 py-3.5">{roleBadge(user.role)}</td>
                        <td className="px-5 py-3.5">{statusBadge(user.status)}</td>
                        <td className="px-5 py-3.5 text-xs text-gray-600 font-mono">{formatDate(user.createdTime)}</td>
                        <td className="px-5 py-3.5 text-xs text-gray-600 font-mono">{formatDate(user.updatedTime)}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <Link
                                to={`/dashboard/users/${user.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition"
                            >
                              <FiEye /> View
                            </Link>
                            {user.status === "ACTIVE" ? (
                              <button
                                onClick={() => setModalUser(user)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 transition"
                              >
                                <FiUserX /> Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => setModalUser(user)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-emerald-600 bg-emerald-50 hover:bg-emerald-100 transition"
                              >
                                <FiUserCheck /> Activate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && totalElements > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-4 border-t border-gray-100 text-sm text-gray-500 gap-4">
                <div className="flex flex-col sm:flex-row items-center gap-4 flex-wrap justify-center text-center sm:text-left">
                  <span>
                    Showing {(page - 1) * pageSize + 1}–
                    {Math.min(page * pageSize, totalElements)} of {totalElements} users
                  </span>
                  <div className="flex items-center gap-2">
                    <label htmlFor="pageSize" className="text-gray-500">Rows per page:</label>
                    <select
                      id="pageSize"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="border border-gray-200 rounded-md text-sm px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-300 bg-white text-gray-700"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap justify-center">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <FiChevronLeft />
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => {
                    // Only show 5 pages around the current page to keep it clean on mobile
                    if (
                      i === 0 ||
                      i === totalPages - 1 ||
                      (i >= page - 2 && i <= page)
                    ) {
                      return (
                        <button
                          key={i}
                          onClick={() => setPage(i + 1)}
                          className={`w-8 h-8 rounded-lg text-xs font-semibold transition ${
                            page === i + 1
                              ? "bg-red-600 text-white shadow"
                              : "hover:bg-gray-100 text-gray-600"
                          }`}
                        >
                          {i + 1}
                        </button>
                      );
                    } else if (
                      (i === 1 && page > 3) ||
                      (i === totalPages - 2 && page < totalPages - 2)
                    ) {
                      return <span key={i} className="px-1 text-gray-400">...</span>;
                    }
                    return null;
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Leaderboard Tab ── */}
        {activeTab === "leaderboard" && (
          <div>
            {leaderboardLoading ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Rank</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">User</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Referral Code</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Total Referrals</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 4 }).map((__, j) => (
                          <td key={j} className="px-5 py-3.5">
                            <div className="h-4 bg-gray-100 rounded animate-pulse" style={{ width: j === 1 ? "160px" : "80px" }} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="py-16 text-center text-gray-400 text-sm">
                <MdLeaderboard className="text-4xl mx-auto mb-3 text-gray-200" />
                No referral data available.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Rank</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">User</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Referral Code</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Total Referrals</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {leaderboard.map((entry) => {
                        const rs = rankStyle[entry.rank] || { bg: "bg-gray-50", text: "text-gray-500", label: `#${entry.rank}` };
                        return (
                          <tr key={entry.userId} className="hover:bg-gray-50/70 transition">
                            <td className="px-5 py-3.5">
                              <span
                                className={`inline-flex items-center justify-center w-8 h-8 rounded-lg font-bold text-sm ${rs.bg} ${rs.text}`}
                              >
                                {entry.rank <= 3 ? rs.label : `#${entry.rank}`}
                              </span>
                            </td>
                            <td className="px-5 py-3.5">
                              <Link
                                to={`/dashboard/users/${entry.userId}`}
                                className="flex items-center gap-3 group"
                              >
                                <Avatar name={entry.username} />
                                <span className="text-sm font-semibold text-gray-800 group-hover:text-red-600 group-hover:underline transition underline-offset-2">
                                  {entry.username}
                                </span>
                              </Link>
                            </td>
                            <td className="px-5 py-3.5">
                              <span className="font-mono text-sm bg-gray-100 text-gray-700 px-2.5 py-1 rounded-md">
                                {entry.referralCode}
                              </span>
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-gray-800">{entry.totalReferrals}</span>
                                <span className="text-xs text-gray-400">referrals</span>
                                {entry.rank === 1 && (
                                  <FiAward className="text-amber-500 text-base" />
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Leaderboard Pagination */}
                {!leaderboardLoading && leaderboardTotalElements > 0 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-4 border-t border-gray-100 text-sm text-gray-500 gap-4">
                    <div className="flex flex-col sm:flex-row items-center gap-4 flex-wrap justify-center text-center sm:text-left">
                      <span>
                        Showing {(leaderboardPage - 1) * leaderboardPageSize + 1}–
                        {Math.min(leaderboardPage * leaderboardPageSize, leaderboardTotalElements)} of {leaderboardTotalElements} entries
                      </span>
                      <div className="flex items-center gap-2">
                        <label htmlFor="lbPageSize" className="text-gray-500">Rows per page:</label>
                        <select
                          id="lbPageSize"
                          value={leaderboardPageSize}
                          onChange={(e) => {
                            setLeaderboardPageSize(Number(e.target.value));
                            setLeaderboardPage(1);
                          }}
                          className="border border-gray-200 rounded-md text-sm px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-300 bg-white text-gray-700"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap justify-center">
                      <button
                        onClick={() => setLeaderboardPage((p) => Math.max(1, p - 1))}
                        disabled={leaderboardPage === 1}
                        className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        <FiChevronLeft />
                      </button>
                      {Array.from({ length: Math.max(1, Math.ceil(leaderboardTotalElements / leaderboardPageSize)) }).map((_, i) => {
                        const lbTotalPages = Math.max(1, Math.ceil(leaderboardTotalElements / leaderboardPageSize));
                        if (
                          i === 0 ||
                          i === lbTotalPages - 1 ||
                          (i >= leaderboardPage - 2 && i <= leaderboardPage)
                        ) {
                          return (
                            <button
                              key={i}
                              onClick={() => setLeaderboardPage(i + 1)}
                              className={`w-8 h-8 rounded-lg text-xs font-semibold transition ${
                                leaderboardPage === i + 1
                                  ? "bg-red-600 text-white shadow"
                                  : "hover:bg-gray-100 text-gray-600"
                              }`}
                            >
                              {i + 1}
                            </button>
                          );
                        } else if (
                          (i === 1 && leaderboardPage > 3) ||
                          (i === lbTotalPages - 2 && leaderboardPage < lbTotalPages - 2)
                        ) {
                          return <span key={i} className="px-1 text-gray-400">...</span>;
                        }
                        return null;
                      })}
                      <button
                        onClick={() => setLeaderboardPage((p) => Math.min(Math.max(1, Math.ceil(leaderboardTotalElements / leaderboardPageSize)), p + 1))}
                        disabled={leaderboardPage === Math.max(1, Math.ceil(leaderboardTotalElements / leaderboardPageSize))}
                        className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        <FiChevronRight />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
